import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../ui/dialog";
import { DashboardExport, parseDashboardExport } from "./dashboardExport";

interface DashboardImportDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onImport: (data: DashboardExport) => void;
}

/**
 * Paste the JSON that "Kopiera som JSON" put on the clipboard – from another
 * organisation, another user or a saved file – and get it as a new private
 * dashboard. The text is checked before anything is created, so a bad paste
 * ends here with a message rather than as a broken dashboard.
 */
const DashboardImportDialog: React.FC<DashboardImportDialogProps> = ({
	open,
	onOpenChange,
	onImport,
}) => {
	const [text, setText] = useState("");
	const [error, setError] = useState<string>();
	useEffect(() => {
		if (open) {
			setText("");
			setError(undefined);
		}
	}, [open]);

	const submit = () => {
		try {
			onImport(parseDashboardExport(text));
			onOpenChange(false);
		} catch (e) {
			setError((e as Error).message);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="z-[1300] w-[min(40rem,calc(100vw-2rem))] max-w-none">
				<DialogHeader>
					<DialogTitle>Klistra in dashboard från JSON</DialogTitle>
					<DialogDescription>
						Klistra in texten från ”Kopiera som JSON”. Dashboarden skapas som en ny, privat
						dashboard hos dig; filter och delning följer inte med.
					</DialogDescription>
				</DialogHeader>
				<textarea
					value={text}
					onChange={(e) => {
						setText(e.target.value);
						setError(undefined);
					}}
					rows={12}
					autoFocus
					spellCheck={false}
					placeholder='{ "format": "origo-dashboard/1", "name": "…", … }'
					className="w-full rounded-md border border-gray-300 p-2 font-mono text-xs focus:border-brand focus:outline-none"
				/>
				{error && <p className="text-sm text-danger">{error}</p>}
				<DialogFooter>
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						className="rounded-md border px-3 py-1.5 text-sm"
					>
						Avbryt
					</button>
					<button
						type="button"
						onClick={submit}
						disabled={!text.trim()}
						className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
					>
						Skapa dashboard
					</button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default DashboardImportDialog;
